import { UpdateIdentityProviderModalComponent } from './UpdateIdentityProviderModal.types';
import { useIntl } from 'react-intl';
import {
  CreateOidcIdentityProviderDto,
  OidcAuthFlow,
  OidcIdentityProviderDto,
} from '../../../../../api-client';
import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Flexbox, Modal, Select, TextField } from '@hiiretail/synergy-ui';
import { ErrorBox } from '../../../../../common/components/ErrorBox/ErrorBox';
import { Controller } from 'react-hook-form';
import { CommonMessages } from '../../../../../common';
import { Option } from '@hiiretail/synergy-ui/dist/types/components/inputs/Select/Select.types';
import { promptOptions } from '../../../utils/propmtOptions';
import { useUpdateOidcProviderForm } from '../../../hooks/Oidc/useUpdateOidcProviderForm';
import { ExternalIdpLogoutConfigForm } from '../../IdentityProvidersList/components/ExternalIdpLogoutConfigForm/ExternalIdpLogoutConfigForm';
import { OidcFlowConfigForm } from '../../IdentityProvidersList/components/OidcFlowConfigForm/OidcFlowConfigForm';

export const UpdateIdentityProviderModal: UpdateIdentityProviderModalComponent = ({
  onClose,
  isOpen,
  updateIdentityProvider,
  isUpdating,
  identityProvider,
  error,
}) => {
  const intl = useIntl();
  const { control, handleSubmit, formState, reset, setValue, trigger, watch } =
    useUpdateOidcProviderForm();

  const [showIdpLogoutConfig, setShowIdpLogoutConfig] = useState<boolean>(false);

  // Watch the flow field to conditionally render fields
  const selectedFlow = watch('flow');

  const handleAddIdpLogoutConfig = useCallback(() => {
    setShowIdpLogoutConfig((value) => !value);
    if (showIdpLogoutConfig) {
      setValue('externalIdpLogoutConfig', undefined);
    } else {
      setValue(
        'externalIdpLogoutConfig.enabled',
        identityProvider.externalIdpLogoutConfig?.enabled ?? true,
      );
      setValue(
        'externalIdpLogoutConfig.redirectUrl',
        identityProvider.externalIdpLogoutConfig?.redirectUrl ?? '',
      );
    }
    trigger('externalIdpLogoutConfig');
  }, [
    identityProvider.externalIdpLogoutConfig?.enabled,
    identityProvider.externalIdpLogoutConfig?.redirectUrl,
    setValue,
    showIdpLogoutConfig,
    trigger,
  ]);

  const onFormSubmit = (updateIdentityProviderDto: CreateOidcIdentityProviderDto) => {
    // @ts-ignore - These fields may not exist in the type definition yet
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { originalFlow, ...identityProviderDto } = updateIdentityProviderDto;
    const baseDtoWithoutPkce = { ...identityProviderDto };

    delete baseDtoWithoutPkce.usePKCE;
    delete baseDtoWithoutPkce.clientSecret;

    if (!identityProviderDto.clientSecret) {
      delete identityProviderDto.clientSecret;
    }

    const finalDto =
      updateIdentityProviderDto.flow === OidcAuthFlow.Code
        ? {
            ...identityProviderDto,
            externalIdpLogoutConfig: updateIdentityProviderDto.externalIdpLogoutConfig ?? undefined,
          }
        : {
            ...baseDtoWithoutPkce,
            externalIdpLogoutConfig: updateIdentityProviderDto.externalIdpLogoutConfig ?? undefined,
          };

    updateIdentityProvider({
      id: identityProvider.id,
      updateIdentityProviderDto: finalDto as OidcIdentityProviderDto,
    });
  };

  useEffect(() => {
    reset({
      name: identityProvider.name,
      clientId: identityProvider.clientId,
      externalIdField: identityProvider.externalIdField,
      issuer: identityProvider.issuer,
      prompt: identityProvider.prompt,
      externalIdpLogoutConfig: identityProvider.externalIdpLogoutConfig ?? null,
      // @ts-ignore - These fields may not exist in the type definition yet
      flow: identityProvider.flow ?? OidcAuthFlow.Implicit,
      // @ts-ignore - originalFlow is used for update-only validation
      originalFlow: identityProvider.flow ?? OidcAuthFlow.Implicit,
      usePKCE: identityProvider.usePKCE ?? false,
      // @ts-ignore
      clientSecret: identityProvider.clientSecret ?? '',
    });
  }, [identityProvider, isOpen, reset, setValue]);

  useEffect(() => {
    setShowIdpLogoutConfig(!!identityProvider.externalIdpLogoutConfig);
    trigger();
  }, [isOpen, identityProvider.externalIdpLogoutConfig, trigger]);

  // Validate clientSecret when switching flows
  useEffect(() => {
    if (selectedFlow === OidcAuthFlow.Code) {
      trigger('clientSecret');
    } else {
      setValue('clientSecret', '');
      trigger('clientSecret');
    }
  }, [selectedFlow, setValue, trigger]);

  return isOpen ? (
    <Modal
      data-testid="update-identity-provider-modal"
      title={`Update ${identityProvider.name}`}
      onClose={onClose}
      open={isOpen}
      footer={
        <Flexbox container justifyContent="flex-end">
          <Button
            type="submit"
            disabled={
              (formState?.errors && !!Object.entries(formState?.errors).length) ||
              Object.keys(formState?.dirtyFields).length < 1 ||
              !formState.isValid ||
              isUpdating
            }
            //@ts-ignore
            onClick={handleSubmit(onFormSubmit)}
          >
            {isUpdating ? 'Updating ...' : 'Update'}
          </Button>
        </Flexbox>
      }
    >
      <ErrorBox error={error} />
      <form>
        <Box mb={2}>
          <Controller
            name="name"
            defaultValue={identityProvider.name}
            control={control}
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                tabIndex={0}
                fullWidth
                label={intl.formatMessage(CommonMessages.name)}
                name="name"
                type="text"
                ref={ref}
                value={value}
                onChange={onChange}
                error={formState.errors?.name?.message as string | undefined}
              />
            )}
          />
        </Box>
        <Box mb={2}>
          <Controller
            name="clientId"
            defaultValue={identityProvider.clientId}
            control={control}
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                tabIndex={0}
                fullWidth
                label={intl.formatMessage(CommonMessages.clientId)}
                name="clientId"
                type="text"
                ref={ref}
                value={value}
                onChange={onChange}
                error={formState.errors?.name?.message as string | undefined}
              />
            )}
          />
        </Box>
        <Box mb={2}>
          <Controller
            name="prompt"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Select
                fullWidth
                label={intl.formatMessage(CommonMessages.prompt)}
                name="prompt"
                value={promptOptions.find((option) => option.value === value) ?? null}
                isSearchable={false}
                isClearable={false}
                placeholder={value}
                initialValue={{ label: 'Select account', value: 'select_account' }}
                onChange={(option: Option | null) => onChange(option?.value)}
                error={formState.errors?.prompt?.message as string | undefined}
                options={promptOptions}
              />
            )}
          />
        </Box>
        <Box mb={2}>
          <Controller
            name="externalIdField"
            defaultValue={identityProvider.externalIdField}
            control={control}
            render={({ field: { value, onChange } }) => (
              <Select
                fullWidth
                label={intl.formatMessage(CommonMessages.externalIdField)}
                name="externalIdField"
                value={value?.value}
                isSearchable={false}
                isClearable={false}
                placeholder={value}
                onChange={(option: Option | null) => onChange(option?.value)}
                error={formState.errors?.externalIdField?.message as string | undefined}
                options={[
                  { label: intl.formatMessage(CommonMessages.uid), value: 'Uid' },
                  { label: intl.formatMessage(CommonMessages.phoneNumber), value: 'PhoneNumber' },
                  { label: intl.formatMessage(CommonMessages.email), value: 'Email' },
                ]}
              />
            )}
          />
        </Box>

        <Box mb={3}>
          <Controller
            name="issuer"
            defaultValue=""
            control={control}
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                fullWidth
                label={intl.formatMessage(CommonMessages.issuer)}
                name="issuer"
                ref={ref}
                type="text"
                value={value}
                onChange={onChange}
                error={formState.errors?.issuer?.message as string | undefined}
              />
            )}
          />
        </Box>

        <Box mb={3}>
          <OidcFlowConfigForm control={control} formState={formState} selectedFlow={selectedFlow} />
        </Box>

        <ExternalIdpLogoutConfigForm
          isOpen={showIdpLogoutConfig}
          handleAddForm={handleAddIdpLogoutConfig}
          formState={formState}
          control={control}
          disableButton={showIdpLogoutConfig && !!identityProvider?.externalIdpLogoutConfig}
        />
      </form>
    </Modal>
  ) : null;
};
